import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  XMarkIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon 
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { ToastNotification } from '@/types';

// Type assertions for Heroicons compatibility
const CheckCircle = CheckCircleIcon as any;
const XCircle = XCircleIcon as any;
const ExclamationTriangle = ExclamationTriangleIcon as any;
const InformationCircle = InformationCircleIcon as any;
const XMark = XMarkIcon as any;

interface ToastProps {
  notification: ToastNotification;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose(notification.id), 300);
    }, notification.duration || 5000);

    return () => clearTimeout(timer);
  }, [notification.id, notification.duration, onClose]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success-400" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-danger-400" />;
      case 'warning':
        return <ExclamationTriangle className="h-5 w-5 text-yellow-400" />;
      case 'info':
        return <InformationCircle className="h-5 w-5 text-primary-400" />;
      default:
        return <InformationCircle className="h-5 w-5 text-primary-400" />;
    }
  };

  const getColorClasses = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-success-50 border-success-200';
      case 'error':
        return 'bg-danger-50 border-danger-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-primary-50 border-primary-200';
      default:
        return 'bg-primary-50 border-primary-200';
    }
  };

  return (
    <div
      className={clsx(
        'transform',
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      <div className={clsx('rounded-xl border p-3 sm:p-4 shadow-lg max-w-xs sm:max-w-sm', getColorClasses())}>
        <div className="flex items-start min-w-0">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="mr-2 sm:mr-3 flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-gray-900 break-words leading-tight">
              {notification.title}
            </p>
            {notification.message && (
              <p className="mt-1 text-xs sm:text-sm text-gray-700 break-words leading-relaxed">
                {notification.message}
              </p>
            )}
          </div>
          <div className="flex-shrink-0 mr-auto">
            <button
              className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 p-1"
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => onClose(notification.id), 300);
              }}
            >
              <span className="sr-only">Close</span>
              <XMark className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast container component
interface ToastContainerProps {
  notifications: ToastNotification[];
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ notifications, onClose }) => {
  return (
    <div className="fixed bottom-0 left-0 z-50 flex flex-col space-y-2 sm:space-y-4 p-3 sm:p-4 md:p-6">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
    </div>
  );
};

// Toast hook and context
const ToastContext = React.createContext<{
  notifications: ToastNotification[];
  addNotification: (notification: Omit<ToastNotification, 'id'>) => void;
  removeNotification: (id: string) => void;
} | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const addNotification = (notification: Omit<ToastNotification, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications(prev => [...prev, { ...notification, id }]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <ToastContext.Provider value={{ notifications, addNotification, removeNotification }}>
      {children}
      <ToastContainer notifications={notifications} onClose={removeNotification} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Helper functions
export const notify = {
  success: (title: string, message?: string) => {
    const { addNotification } = useToast();
    addNotification({ type: 'success', title, message });
  },
  error: (title: string, message?: string) => {
    const { addNotification } = useToast();
    addNotification({ type: 'error', title, message });
  },
  warning: (title: string, message?: string) => {
    const { addNotification } = useToast();
    addNotification({ type: 'warning', title, message });
  },
  info: (title: string, message?: string) => {
    const { addNotification } = useToast();
    addNotification({ type: 'info', title, message });
  },
}; 